/**
 * ConfigExportImportCard - Sauvegarde et restauration de la configuration
 * Extrait de Profile.js pour améliorer la maintenabilité
 * AMIRAUX UNIQUEMENT
 */
import React, { useState } from 'react';
import { Settings, Download, UploadCloud, RefreshCw, AlertTriangle, Code, Shield } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ConfigExportImportCard = ({
  exporting,
  importing,
  importFile,
  importData,
  showImportDialog,
  setShowImportDialog,
  onExport,
  onImportFileSelect,
  onImport,
  onCloseDialog
}) => {
  const [exportingCode, setExportingCode] = useState(false);

  const handleExportSourceCode = async () => {
    setExportingCode(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/api/source-code/export`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur lors de l\'export');
      }
      
      // Récupérer le nom du fichier depuis les headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename=(.+)/);
      const filename = filenameMatch ? filenameMatch[1] : 'autoedito_source.zip';
      
      // Télécharger le fichier
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Code source exporté avec succès', {
        description: 'Les fichiers sensibles (.env, secrets) ont été exclus automatiquement.'
      });
    } catch (error) {
      console.error('Export code source error:', error);
      toast.error('Erreur lors de l\'export du code source', {
        description: error.message
      });
    } finally {
      setExportingCode(false);
    }
  };

  return (
    <Card className="relative bg-slate-950/40 backdrop-blur-xl border-slate-800/60">
      <CardHeader>
        <CardTitle className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-cyan-400 flex items-center">
          <Settings className="w-5 h-5 md:w-6 md:h-6 mr-2 md:mr-3" />
          Sauvegarde de la configuration
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-slate-500">
          Exportez ou importez l&apos;ensemble de vos sources de surveillance (Radar, Sonar, Cible) et sites WordPress.
          Conservez une copie locale pour la restaurer entre les deploiements.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export Config */}
          <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
            <h3 className="text-sm text-cyan-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
              <Download className="w-4 h-4" />Exporter Config
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Telecharge un fichier JSON contenant toute la configuration (mots de passe masques).
            </p>
            <Button onClick={onExport} disabled={exporting}
              className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
              data-testid="btn-export-config">
              {exporting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {exporting ? 'Export en cours...' : 'Telecharger la configuration'}
            </Button>
          </div>

          {/* Import */}
          <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-800">
            <h3 className="text-sm text-cyan-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
              <UploadCloud className="w-4 h-4" />Importer
            </h3>
            <p className="text-xs text-slate-500 mb-3">
              Restaurez une configuration depuis un fichier JSON precedemment exporte.
            </p>
            <label className="block">
              <input type="file" accept=".json" onChange={onImportFileSelect} className="hidden"
                data-testid="input-import-config" />
              <Button as="span" className="w-full bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 border border-amber-500/50 cursor-pointer"
                onClick={(e) => e.currentTarget.parentElement.querySelector('input').click()}
                data-testid="btn-import-config">
                <UploadCloud className="w-4 h-4 mr-2" />
                {importFile ? importFile.name : 'Charger un fichier JSON'}
              </Button>
            </label>
          </div>
        </div>

        {/* Export Code Source Sécurisé */}
        <div className="p-4 bg-emerald-950/30 rounded-lg border border-emerald-500/30">
          <h3 className="text-sm text-emerald-400 uppercase tracking-wider font-semibold mb-2 flex items-center gap-2">
            <Code className="w-4 h-4" />
            <Shield className="w-4 h-4" />
            Export Code Source Securise
          </h3>
          <p className="text-xs text-slate-400 mb-3">
            Telecharge le code source complet (backend + frontend) en ZIP. 
            <span className="text-emerald-400 font-medium"> Les fichiers sensibles (.env, secrets, mots de passe) sont automatiquement exclus.</span>
          </p>
          <Button onClick={handleExportSourceCode} disabled={exportingCode}
            className="w-full bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"
            data-testid="btn-export-source-code">
            {exportingCode ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Code className="w-4 h-4 mr-2" />}
            {exportingCode ? 'Generation du ZIP...' : 'Telecharger le code source'}
          </Button>
          <p className="text-xs text-slate-600 mt-2 flex items-center gap-1">
            <Shield className="w-3 h-3" />
            Exclut : .env, node_modules, __pycache__, uploads, fichiers de config sensibles
          </p>
        </div>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) onCloseDialog(); else setShowImportDialog(open); }}>
          <DialogContent className="bg-slate-950 border-cyan-500/50 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-orbitron text-cyan-400">Importer la configuration</DialogTitle>
            </DialogHeader>
            {importData && (
              <div className="space-y-4 mt-2">
                <div className="p-3 bg-slate-900/50 rounded border border-slate-800 text-sm text-slate-300 space-y-1">
                  <p className="text-xs text-slate-500 mb-2">Contenu du fichier (v{importData.version || '?'}) :</p>
                  {importData.security_note && (
                    <p className="text-xs text-amber-400 mb-2 flex items-center gap-1">
                      <Shield className="w-3 h-3" /> {importData.security_note}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                    {[
                      ['Sites WordPress', 'wordpress_sites'],
                      ['Sources Radar', 'radar_sources'],
                      ['Sources Sonar', 'sonar_sources'],
                      ['Sources Cible', 'cible_sources'],
                      ['Utilisateurs', 'users'],
                      ['Noms auteurs', 'user_author_mappings'],
                      ['Tribunes', 'email_tribunes'],
                      ['Interviews', 'interviews'],
                      ['Partenaires', 'partenaire_requests'],
                      ['Config Amazon', 'amazon_config'],
                      ['Publications', 'publications'],
                      ['Articles', 'articles'],
                      ['Credentials WP', 'user_wp_credentials'],
                      ['File attente', 'publication_queue'],
                    ].map(([label, key]) => {
                      const count = (importData[key] || []).length;
                      return count > 0 ? (
                        <p key={key}>{label} : <span className="text-cyan-400 font-mono">{count}</span></p>
                      ) : null;
                    })}
                  </div>
                  {importData.exported_at && (
                    <p className="text-xs text-slate-600 mt-2">Exporte le : {new Date(importData.exported_at).toLocaleString('fr-FR')}</p>
                  )}
                </div>

                <div className="p-3 bg-amber-500/5 border border-amber-500/30 rounded flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-300">
                    <strong>Remplacer</strong> supprimera toutes les donnees et les remplacera par le fichier.<br />
                    <strong>Fusionner</strong> ajoutera les nouveaux elements et mettra a jour les existants.
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button onClick={() => onImport('merge')} disabled={importing}
                    className="flex-1 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-500/50"
                    data-testid="btn-import-merge">
                    {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Fusionner
                  </Button>
                  <Button onClick={() => onImport('replace')} disabled={importing}
                    className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/50"
                    data-testid="btn-import-replace">
                    {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Tout remplacer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default ConfigExportImportCard;
